import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import './styles/globals.css';
import { reportError } from './utils/errorReporter.js';
import { isChunkLoadError, tryAutoReload, showChunkFailureNotice } from './utils/chunkReload.js';

// ── Global error handlers ──────────────────────────────────────────────────
// React's ErrorBoundary catches render/lifecycle errors. These two listeners
// catch everything outside React: async errors, unhandled promise rejections,
// library code, etc.
//
// Errors are reported silently in the background — the user never sees a
// blocking overlay. If the error is fatal (UI stops responding), the app's
// own UI will surface a problem; we don't need to add another layer here.

// A chunk that failed to load — a stale chunk after a new deployment (the
// browser cached the old entrypoint but the hashed files are gone), or a chunk
// fetch that stalled past lazyWithTimeout's limit. A failed dynamic import()
// can fire here BEFORE React's lazy()/Suspense machinery re-throws it into
// ErrorBoundary, so recover here too — through the SAME capped guard
// (src/utils/chunkReload.js). This used to reload unconditionally, so a stall
// that persisted reloaded the app forever behind a blank page (GH #7430).
function recoverFromChunkError(message, stack) {
  if (tryAutoReload()) return;
  showChunkFailureNotice();
  reportError({ message, stack, context: 'chunk-load-reload-cap-spent' });
}

window.addEventListener('error', (event) => {
  const error = event.error;
  const message = error?.message || event.message || 'Uncaught error';
  const stack = error?.stack ?? undefined;
  console.error('[GlobalErrorHandler] Uncaught error:', error || message);
  if (isChunkLoadError(message)) {
    recoverFromChunkError(message, stack);
    return;
  }
  reportError({ message, stack, context: 'window.onerror' });
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason?.message || String(reason ?? 'Unhandled promise rejection');
  const stack = reason?.stack ?? undefined;
  console.error('[GlobalErrorHandler] Unhandled promise rejection:', reason);
  if (isChunkLoadError(message)) {
    recoverFromChunkError(message, stack);
    return;
  }
  reportError({ message, stack, context: 'unhandledrejection' });
});

// ── App bootstrap ──────────────────────────────────────────────────────────

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// cache bust 202603152225
